import type {
  InputIssueCode,
  NormalizedProjectPath,
  PathInputIssue,
  ProjectPathKind,
} from '../types';

export class PathNormalizationError extends Error {
  readonly code: InputIssueCode;
  readonly input: string;

  constructor(code: InputIssueCode, input: string, message: string) {
    super(message);
    this.name = 'PathNormalizationError';
    this.code = code;
    this.input = input;
  }
}

export interface NormalizePathOptions {
  readonly kind?: ProjectPathKind | 'infer';
}

export interface NormalizedPathCollection {
  readonly paths: readonly NormalizedProjectPath[];
  readonly issues: readonly PathInputIssue[];
}

const WINDOWS_DRIVE_PATH = /^[A-Za-z]:/u;

/**
 * Converts a user-provided repository path to the slash-separated form expected
 * by Git and `ignore`. Inputs are never resolved against the current machine.
 */
export function normalizeProjectPath(input: string, options: NormalizePathOptions = {}): string {
  if (typeof input !== 'string') {
    throw new PathNormalizationError(
      'invalid-path',
      String(input),
      'Project paths must be strings.',
    );
  }

  if (input.length === 0 || input.trim().length === 0) {
    throw new PathNormalizationError(
      'empty-path',
      input,
      'Empty paths are not part of the project tree.',
    );
  }

  if (input.includes('\0')) {
    throw new PathNormalizationError(
      'invalid-path',
      input,
      'Paths cannot contain a null character.',
    );
  }

  const slashPath = input.replaceAll('\\', '/');
  if (
    slashPath.startsWith('/') ||
    slashPath.startsWith('//') ||
    WINDOWS_DRIVE_PATH.test(slashPath)
  ) {
    throw new PathNormalizationError(
      'absolute-path',
      input,
      'Use a path relative to the project root; absolute paths are not accepted.',
    );
  }

  const requestedKind = options.kind ?? 'infer';
  const endsAsDirectory =
    requestedKind === 'directory' ||
    (requestedKind === 'infer' && (slashPath.endsWith('/') || slashPath.endsWith('/.')));
  const segments: string[] = [];

  for (const segment of slashPath.split('/')) {
    if (segment.length === 0 || segment === '.') {
      continue;
    }

    if (segment === '..') {
      throw new PathNormalizationError(
        'path-traversal',
        input,
        'Paths containing `..` would leave the project root and are not accepted.',
      );
    }

    segments.push(segment);
  }

  if (segments.length === 0) {
    throw new PathNormalizationError(
      'empty-path',
      input,
      'The project root itself is not an analyzable path.',
    );
  }

  const normalized = segments.join('/');
  return endsAsDirectory ? `${normalized}/` : normalized;
}

/** Normalizes, de-duplicates, and fills in directory ancestors. */
export function normalizeProjectPaths(inputs: readonly string[]): NormalizedPathCollection {
  const explicit = new Map<string, NormalizedProjectPath>();
  const issues: PathInputIssue[] = [];

  for (const input of inputs) {
    try {
      const path = normalizeProjectPath(input);
      if (explicit.has(path)) {
        issues.push({
          code: 'duplicate-path',
          input,
          message: `Duplicate path \`${path}\` was analyzed once.`,
        });
        continue;
      }

      explicit.set(path, {
        path,
        kind: path.endsWith('/') ? 'directory' : 'file',
        inferred: false,
      });
    } catch (error: unknown) {
      if (error instanceof PathNormalizationError) {
        issues.push({ code: error.code, input: error.input, message: error.message });
        continue;
      }
      throw error;
    }
  }

  const ancestors = new Set<string>();
  for (const item of explicit.values()) {
    const bare = item.path.endsWith('/') ? item.path.slice(0, -1) : item.path;
    const segments = bare.split('/');
    for (let index = 1; index < segments.length; index += 1) {
      ancestors.add(`${segments.slice(0, index).join('/')}/`);
    }
  }

  // A user may provide an extensionless directory without a trailing slash.
  // If it is also an ancestor, the tree itself proves that it is a directory.
  for (const ancestor of ancestors) {
    const bare = ancestor.slice(0, -1);
    const ambiguous = explicit.get(bare);
    if (ambiguous !== undefined) {
      explicit.delete(bare);
      explicit.set(ancestor, { path: ancestor, kind: 'directory', inferred: false });
    }
  }

  const all = new Map(explicit);
  for (const ancestor of ancestors) {
    if (!all.has(ancestor)) {
      all.set(ancestor, { path: ancestor, kind: 'directory', inferred: true });
    }
  }

  return {
    paths: [...all.values()].sort((left, right) => comparePaths(left.path, right.path)),
    issues,
  };
}

export function parentDirectories(path: string): readonly string[] {
  const normalized = normalizeProjectPath(path);
  const bare = normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
  const segments = bare.split('/');
  const parents: string[] = [];
  for (let index = 1; index < segments.length; index += 1) {
    parents.push(`${segments.slice(0, index).join('/')}/`);
  }
  return parents;
}

export function sourceDirectory(sourcePath: string): string {
  const normalized = normalizeProjectPath(sourcePath, { kind: 'file' });
  const separator = normalized.lastIndexOf('/');
  return separator === -1 ? '' : normalized.slice(0, separator);
}

export function isPathInsideDirectory(path: string, directory: string): boolean {
  if (directory.length === 0) {
    return true;
  }
  const barePath = path.endsWith('/') ? path.slice(0, -1) : path;
  return barePath === directory || barePath.startsWith(`${directory}/`);
}

export function relativeToDirectory(path: string, directory: string): string | null {
  if (!isPathInsideDirectory(path, directory)) {
    return null;
  }
  if (directory.length === 0) {
    return path;
  }
  const offset = directory.length + 1;
  return path.slice(offset);
}

function comparePaths(left: string, right: string): number {
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}
